import { useTranslation } from "react-i18next";
import { TriangleAlert, Users } from "lucide-react";
import { Typography } from "@akuna/ui";
import { colorDotClass, colorDotStyle } from "../accommodations/colors";
import type { PanelPackGroup, PanelPet, PanelPetCardDetails } from "./panelModel";
import { PetCard } from "./PetCard";

export interface PackGroupProps {
  group: PanelPackGroup;
  overnightEnabled: boolean;
  onCheckIn: (pet: PanelPet) => void;
  onFollowUp: (pet: PanelPet) => void;
  onOvernight: (pet: PanelPet) => void;
  detailsByPetId?: Map<string, PanelPetCardDetails>;
}

export function PackGroup({ group, overnightEnabled, onCheckIn, onFollowUp, onOvernight, detailsByPetId }: PackGroupProps) {
  const { t } = useTranslation("daycareHotel");
  const headerClass = colorDotClass(group.color);

  return (
    <section className="overflow-hidden rounded-2xl border border-border bg-card">
      <header
        className={`flex flex-wrap items-center gap-3 px-4 py-3.5 text-akuna-cream ${headerClass}`}
        style={colorDotStyle(group.color)}
      >
        <Users aria-hidden="true" size={18} className="shrink-0" />
        <Typography variant="h5" className="text-akuna-cream">{group.name ?? t("panel.unpackedGroup")}</Typography>
        <Typography variant="body-sm" className="text-akuna-cream/90">
          {t("panel.packCount", { count: group.pets.length })}
        </Typography>
        {group.hasAggressivePet && (
          <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-akuna-cream/20 px-2.5 py-1">
            <TriangleAlert aria-hidden="true" size={14} className="text-akuna-cream" />
            <Typography variant="caption" className="font-semibold text-akuna-cream">{t("panel.aggressiveWarning")}</Typography>
          </span>
        )}
      </header>

      <div className="p-4">
        {group.pets.length === 0 ? (
          <Typography variant="body-sm" className="py-6 text-center text-muted-foreground">
            {t("panel.packEmpty")}
          </Typography>
        ) : (
          <div className="grid gap-4 sm:grid-cols-[repeat(auto-fill,minmax(300px,1fr))]">
            {group.pets.map((pet) => (
              <PetCard
                key={pet.reservationId}
                pet={pet}
                details={detailsByPetId?.get(pet.petId)}
                overnightEnabled={overnightEnabled}
                onCheckIn={onCheckIn}
                onFollowUp={onFollowUp}
                onOvernight={onOvernight}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
